import { resendCurrentScreen } from "./analytics";
import { start } from "./state";
import type { Destination } from "./types";

/**
 * How long `initAnalytics` holds up its caller (boot waits on it). A slower
 * destination init goes on in the background: events keep queueing and are
 * sent once it succeeds, or dropped if it fails.
 */
const MAX_INIT_WAIT_MS = 10_000;

/**
 * Starts sending events to `destination`, then flushes events tracked before init.
 * Call once, from the root layout. Resolves when the destination is ready, when
 * its init fails, or after 10s, whichever is first; never rejects. If init fails,
 * the error is logged and queued events are dropped. While an init is in flight,
 * repeat calls wait on it and their destination is ignored; after success, calls
 * are ignored. After a failure, a new call retries with the current user and screen.
 */
export function initAnalytics(options: { destination: Destination }): Promise<void> {
  const starting = start(options.destination, resendCurrentScreen);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const waited = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, MAX_INIT_WAIT_MS);
  });
  return Promise.race([starting, waited]).finally(() => {
    clearTimeout(timer);
  });
}
